/**
 * «Mitt sjøkart» — personlig kartplotter på regattaen.
 *
 * Viser KUN din egen båt på Kartverkets sjøkart, og kartet sentrerer automatisk.
 * Posisjonen hentes fra båtens maskinvare-tracker hvis den er online; ellers fra
 * telefonens GPS (som da også sendes videre til live-kartet).
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api, type TrackPost } from '../lib/api.ts';
import { useGps, GOOD_ACCURACY_M } from '../lib/useGps.ts';

const DEFAULT_CENTER: [number, number] = [58.34, 8.59];
const KV_ATTR = '© Kartverket';

type EffFix = {
  lat: number;
  lon: number;
  cog: number | null;
  sogKn: number;
  accuracy: number | null;
  source: 'tracker' | 'phone';
};

function meIcon(cog: number | null): L.DivIcon {
  const rot = cog ?? 0;
  return L.divIcon({
    className: '',
    html:
      `<div style="transform:rotate(${rot}deg)">` +
      `<svg width="30" height="30" viewBox="-15 -15 30 30">` +
      `<polygon points="0,-13 8,11 0,6 -8,11" fill="#0ea5e9" stroke="#fff" stroke-width="1.5"/>` +
      `</svg></div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

export function MyChartPage() {
  const { slug } = useParams<{ slug: string }>();
  const gps = useGps();

  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const trailRef = useRef<L.Polyline | null>(null);
  const trailPts = useRef<[number, number][]>([]);
  const effRef = useRef<EffFix | null>(null);
  const followingRef = useRef(true);

  const [following, setFollowing] = useState(true);
  useEffect(() => {
    followingRef.current = following;
  }, [following]);

  const sail = localStorage.getItem('rc_sail') ?? '';
  const password = localStorage.getItem('rc_pw') ?? '';
  const canSend = sail.trim().length > 0 && password.length > 0 && !!slug;

  // Maskinvare-tracker for båten (om online).
  const trackerQuery = useQuery({
    queryKey: ['myboat', slug, sail.trim().toUpperCase()],
    queryFn: () => api.myBoatPosition(slug!, sail.trim()),
    enabled: !!slug && sail.trim().length > 0,
    refetchInterval: 4000,
  });
  const tracker = trackerQuery.data?.tracker ?? null;

  // Effektiv posisjon: tracker (om online) ellers telefonens GPS.
  const eff: EffFix | null = tracker
    ? { lat: tracker.lat, lon: tracker.lon, cog: tracker.cog, sogKn: tracker.sogKn ?? 0, accuracy: null, source: 'tracker' }
    : gps.goodFix && gps.fix
    ? { lat: gps.fix.lat, lon: gps.fix.lon, cog: gps.fix.cog, sogKn: gps.fix.sogKn, accuracy: gps.fix.accuracy, source: 'phone' }
    : null;
  useEffect(() => {
    effRef.current = eff;
  });

  // Telefon-sending kun når det IKKE finnes en online tracker.
  const [sending, setSending] = useState(canSend);
  const [sent, setSent] = useState(0);

  // Init kart (GPS startes betinget under).
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const map = L.map(mapEl.current).setView(DEFAULT_CENTER, 14);
    const sjokart = L.tileLayer(
      'https://cache.kartverket.no/v1/wmts/1.0.0/sjokartraster/default/webmercator/{z}/{y}/{x}.png',
      { maxZoom: 18, attribution: KV_ATTR },
    );
    const topo = L.tileLayer(
      'https://cache.kartverket.no/v1/wmts/1.0.0/topo/default/webmercator/{z}/{y}/{x}.png',
      { maxZoom: 18, attribution: KV_ATTR },
    );
    const osm = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    });
    sjokart.addTo(map);
    L.control.layers({ Sjøkart: sjokart, Topografisk: topo, OpenStreetMap: osm }, {}, { collapsed: true }).addTo(map);
    map.on('dragstart', () => setFollowing(false));
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 100);

    return () => {
      map.remove();
      mapRef.current = null;
      gps.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Telefon-GPS kun når det ikke finnes en online tracker (sparer batteri/tillatelse).
  const hasTracker = !!tracker;
  useEffect(() => {
    if (trackerQuery.isLoading) return; // vent til vi vet om tracker finnes
    if (hasTracker) {
      if (gps.status === 'active') gps.stop();
      setSending(false);
    } else if (gps.status !== 'active') {
      gps.start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTracker, trackerQuery.isLoading]);

  // Oppdater markør + spor + sentrer (fra effektiv posisjon)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !eff) return;
    const pos: [number, number] = [eff.lat, eff.lon];

    if (!markerRef.current) {
      markerRef.current = L.marker(pos, { icon: meIcon(eff.cog) }).addTo(map);
    } else {
      markerRef.current.setLatLng(pos);
      markerRef.current.setIcon(meIcon(eff.cog));
    }

    const tp = trailPts.current;
    const last = tp[tp.length - 1];
    if (!last || last[0] !== pos[0] || last[1] !== pos[1]) tp.push(pos);
    if (tp.length > 150) tp.shift();
    if (!trailRef.current) {
      trailRef.current = L.polyline(tp, { color: '#0ea5e9', weight: 2, opacity: 0.6 }).addTo(map);
    } else {
      trailRef.current.setLatLngs(tp);
    }

    if (followingRef.current) map.setView(pos, map.getZoom(), { animate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eff?.lat, eff?.lon, eff?.cog]);

  // Send telefon-posisjon videre (kun uten tracker, hvis registrert og på)
  useEffect(() => {
    if (!sending || !canSend || !slug || hasTracker) return;
    const id = setInterval(async () => {
      const f = gps.fix;
      if (!f || f.accuracy == null || f.accuracy > GOOD_ACCURACY_M) return;
      const body: TrackPost = {
        raceSlug: slug,
        sailNumber: sail.trim().toUpperCase(),
        password,
        lat: f.lat,
        lon: f.lon,
        sogKn: f.sogKn,
        cog: f.cog,
        accuracy: f.accuracy,
        recordedAt: f.recordedAt,
      };
      try {
        const r = await api.postTrack(body);
        setSent((c) => c + 1);
        if (r.finished) setSending(false);
      } catch {
        /* forbigående — prøver igjen */
      }
    }, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sending, canSend, slug, sail, password, hasTracker]);

  function recenter() {
    setFollowing(true);
    const f = effRef.current;
    if (f && mapRef.current) mapRef.current.setView([f.lat, f.lon], mapRef.current.getZoom());
  }

  const waitingText = hasTracker ? '📡 Venter på tracker…' : '📡 Venter på GPS…';

  return (
    <div
      className="fixed inset-x-0 top-0 z-20 h-screen flex flex-col bg-slate-950"
      style={{ height: '100dvh' }}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800">
        <button
          type="button"
          onClick={() => window.close()}
          className="text-slate-400 text-sm shrink-0"
        >
          ✕ Lukk
        </button>
        <h1 className="font-semibold">🧭 Mitt sjøkart</h1>
        <span className="text-xs text-slate-500 shrink-0">
          {eff ? `${eff.sogKn.toFixed(1)} kn` : '…'}
        </span>
      </div>

      <div className="flex-1 relative">
        <div ref={mapEl} className="absolute inset-0" />

        <div className="absolute top-2 left-2 z-[1000] bg-slate-900/85 border border-slate-700 rounded-lg px-3 py-2 text-xs">
          {eff ? (
            <>
              <div className={eff.source === 'tracker' ? 'text-emerald-400 mb-1' : 'text-slate-400 mb-1'}>
                {eff.source === 'tracker' ? '🛰️ GPS-tracker' : '📱 Telefon-GPS'}
              </div>
              <div>
                SOG <span className="font-mono">{eff.sogKn.toFixed(1)}</span> kn
              </div>
              <div>
                Kurs{' '}
                <span className="font-mono">
                  {eff.cog != null ? `${Math.round(eff.cog).toString().padStart(3, '0')}°` : '—'}
                </span>
              </div>
              {eff.accuracy != null && <div className="text-slate-500">±{Math.round(eff.accuracy)} m</div>}
              {eff.source === 'tracker' && tracker?.satellites != null && (
                <div className="text-slate-500">{tracker.satellites} sat</div>
              )}
            </>
          ) : (
            <div className="text-amber-300">{waitingText}</div>
          )}
        </div>

        {!following && (
          <button
            onClick={recenter}
            className="absolute bottom-4 right-4 z-[1000] bg-sky-600 hover:bg-sky-500 text-white rounded-full px-4 py-2 text-sm font-semibold shadow-lg"
          >
            📍 Sentrer
          </button>
        )}
      </div>

      {hasTracker ? (
        <div className="px-3 py-2 border-t border-slate-800 text-xs text-emerald-300/80 text-center">
          🛰️ Posisjon fra båtens GPS-tracker — sendes automatisk til live-kartet.
        </div>
      ) : (
        canSend && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-slate-800 text-xs">
            <span className="text-slate-400">
              {sending ? `📡 Sender posisjon (${sent})` : 'Sender ikke posisjon'}
              {gps.wakeLockOn && ' · 📱 skjerm-på'}
            </span>
            <button
              onClick={() => setSending((s) => !s)}
              className={`px-3 py-1 rounded font-semibold ${
                sending ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-slate-700 hover:bg-slate-600'
              }`}
            >
              {sending ? 'Stopp sending' : 'Send posisjon'}
            </button>
          </div>
        )
      )}
    </div>
  );
}
